import { Container, Button, Label, SelectInput, SliderInput } from '@playcanvas/pcui/react';
import React from 'react';

import { extract } from '../../helpers';
import { SetProperty, ObserverData } from '../../types';
import { Slider, Toggle, Select, ColorPickerControl, ToggleColor, Numeric } from '../components';

const rgbToArr = (rgb: { r: number, g: number, b: number }) => [rgb.r, rgb.g, rgb.b, 1];
const arrToRgb = (arr: number[]) => {
    return { r: arr[0], g: arr[1], b: arr[2] };
};

class CameraPanel extends React.Component <{
    observerData: ObserverData,
    setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{
        observerData: ObserverData;
        setProperty: SetProperty; }>): boolean {

        const keys = ['ui', 'debug', 'animation.playing', 'camera', 'skybox'];
        const a = extract(nextProps.observerData, keys);
        const b = extract(this.props.observerData, keys);
        return JSON.stringify(a) !== JSON.stringify(b);
    }

    render() {
        const props = this.props;
        return (
            <div className='popup-panel-parent'>
                <Container class='popup-panel' flex hidden={props.observerData.ui.active !== 'camera'}>
                    <div className='popup-panel-header'>
                        <Label text='Camera' class='popup-panel-heading' />
                        <Button 
                            class='popup-panel-close' 
                            icon='E132' 
                            onClick={() => props.setProperty('ui.active', null)}
                        />
                    </div>
                    <Slider
                        label='Fov'
                        precision={0}
                        min={35}
                        max={150}
                        value={props.observerData.camera.fov}
                        setProperty={(value: number) => props.setProperty('camera.fov', value)} />
                    <Select
                        label='Tonemap'
                        type='string'
                        options={['None', 'Linear', 'Neutral', 'Filmic', 'Hejl', 'ACES', 'ACES2'].map(v => ({ v, t: v }))}
                        value={props.observerData.camera.tonemapping}
                        setProperty={(value: number) => props.setProperty('camera.tonemapping', value)} />
                    <Slider
                        label='Scene Exposure'
                        precision={2} min={0.1} max={4}
                        value={props.observerData.camera.exposure}
                        setProperty={(value: number) => props.setProperty('camera.exposure', value)} />
                    <Select
                        label='Pixel Scale'
                        value={props.observerData.camera.pixelScale}
                        type='number'
                        options={[1, 2, 4, 8, 16].map(v => ({ v: v, t: Number(v).toString() }))}
                        setProperty={(value: number) => props.setProperty('camera.pixelScale', value)} />
                    <Toggle
                        label='Multisample'
                        value={props.observerData.camera.multisample}
                        enabled={props.observerData.camera.multisampleSupported}
                        setProperty={(value: boolean) => props.setProperty('camera.multisample', value)}
                    />
                    <Toggle
                        label='High Quality'
                        value={props.observerData.camera.hq}
                        enabled={!props.observerData.animation.playing && !props.observerData.debug.stats }
                        setProperty={(value: boolean) => props.setProperty('camera.hq', value)}
                    />
                    <Select
                        label='Camera Mode'
                        type='string'
                        options={[
                            { v: 'orbit', t: '🎯 Orbit Cam' },
                            { v: 'free', t: '✈️ Free Cam' }
                        ]}
                        value={props.observerData.camera.uiCameraMode || 'orbit'}
                        setProperty={(value: string) => props.setProperty('camera.uiCameraMode', value)}
                    />
                    <Container class='camera-speed-display' flexDirection='column'>
                        <Label text='Current Speed:' class='speed-label-header' />
                        <Label text={`${props.observerData.camera.currentSpeed.toFixed(4)}`} class='speed-label' />
                    </Container>
                </Container>
            </div>
        );
    }
}

class SkyboxPanel extends React.Component <{
    skyboxData: ObserverData['skybox'],
    uiData: ObserverData['ui'],
    setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{
        skyboxData: ObserverData['skybox'];
        uiData: ObserverData['ui'];
        setProperty: SetProperty; }>): boolean {

        return JSON.stringify(nextProps.skyboxData) !== JSON.stringify(this.props.skyboxData) ||
                JSON.stringify(nextProps.uiData) !== JSON.stringify(this.props.uiData);
    }

    render() {
        const props = this.props;

        return (
            <div className='popup-panel-parent'>
                <Container class='popup-panel' flex hidden={props.uiData.active !== 'skybox'}>
                    <div className='popup-panel-header'>
                        <Label text='Sky' class='popup-panel-heading' />
                        <Button 
                            class='popup-panel-close' 
                            icon='E132' 
                            onClick={() => props.setProperty('ui.active', null)}
                        />
                    </div>
                    <Select
                        label='Environment'
                        type='string'
                        options={JSON.parse(props.skyboxData.options)}
                        value={props.skyboxData.value}
                        setProperty={(value: string) => props.setProperty('skybox.value', value)} />
                    {/* Removed Generate Basic Skybox and View Skybox buttons - skybox is auto-generated for screenshots */}
                    <Slider
                        label='Exposure'
                        value={props.skyboxData.exposure}
                        setProperty={(value: number) => props.setProperty('skybox.exposure', value)}
                        precision={2}
                        min={-6}
                        max={6}
                        enabled={props.skyboxData.value !== 'None'} />
                    <Slider
                        label='Rotation'
                        precision={0}
                        min={-180}
                        max={180}
                        value={props.skyboxData.rotation}
                        setProperty={(value: number) => props.setProperty('skybox.rotation', value)}
                        enabled={props.skyboxData.value !== 'None'} />
                    <Select
                        label='Background'
                        type='string'
                        options={['Solid Color', 'Infinite Sphere', 'Projective Dome', 'Projective Box'].map(v => ({ v, t: v }))}
                        value={props.skyboxData.background}
                        setProperty={(value: string) => props.setProperty('skybox.background', value)}
                        enabled={props.skyboxData.value !== 'None'} />
                    <ColorPickerControl
                        label='Background Color'
                        value={rgbToArr(props.skyboxData.backgroundColor)}
                        setProperty={(value: number[]) => props.setProperty('skybox.backgroundColor', arrToRgb(value))}
                        enabled={props.skyboxData.value === 'None' || props.skyboxData.background === 'Solid Color'} />
                    <Slider
                        label='Blur'
                        // type='number'
                        // options={[0, 1, 2, 3, 4, 5].map(v => ({ v: v, t: v === 0 ? 'Disabled' : `Mip ${v}` }))}
                        value={props.skyboxData.blur}
                        setProperty={(value: number) => props.setProperty('skybox.blur', value)}
                        enabled={props.skyboxData.value !== 'None' && props.skyboxData.background === 'Infinite Sphere'}
                        min={0}
                        max={5}
                        precision={0}
                        step={1}/>
                    <Numeric
                        label='Scale'
                        value={props.skyboxData.domeProjection.domeRadius}
                        setProperty={(value: number) => props.setProperty('skybox.domeProjection.domeRadius', value)}
                        min={0}
                        max={1000}
                        enabled={props.skyboxData.value !== 'None' && ['Projective Dome', 'Projective Box'].indexOf(props.skyboxData.background) !== -1} />
                    <Slider
                        label='Tripod Offset'
                        value={props.skyboxData.domeProjection.tripodOffset}
                        setProperty={(value: number) => props.setProperty('skybox.domeProjection.tripodOffset', value)}
                        min={0}
                        max={1}
                        precision={2}
                        enabled={props.skyboxData.value !== 'None' && ['Projective Dome', 'Projective Box'].indexOf(props.skyboxData.background) !== -1} />
                </Container>
            </div>
        );
    }
}

class LightPanel extends React.Component <{
    lightData: ObserverData['light'],
    uiData: ObserverData['ui'],
    shadowCatcherData: ObserverData['shadowCatcher'],
    setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{
        lightData: ObserverData['light'];
        uiData: ObserverData['ui'];
        setProperty: SetProperty; }>): boolean {

        return JSON.stringify(nextProps.lightData) !== JSON.stringify(this.props.lightData) ||
               JSON.stringify(nextProps.uiData) !== JSON.stringify(this.props.uiData);
    }

    render() {
        const props = this.props;

        return (
            <div className='popup-panel-parent'>
                <Container class='popup-panel' flex hidden={props.uiData.active !== 'light'}>
                    <div className='popup-panel-header'>
                        <Label text='Light' class='popup-panel-heading' />
                        <Button 
                            class='popup-panel-close' 
                            icon='E132' 
                            onClick={() => props.setProperty('ui.active', null)}
                        />
                    </div>
                    <Toggle
                        label='Enabled'
                        value={props.lightData.enabled}
                        setProperty={(value: boolean) => props.setProperty('light.enabled', value)} />
                    <Toggle label='Follow Camera'
                        value={props.lightData.follow}
                        setProperty={(value: boolean) => props.setProperty('light.follow', value)} />
                    <ColorPickerControl
                        label='Color'
                        value={rgbToArr(props.lightData.color)}
                        setProperty={(value: number[]) => props.setProperty('light.color', arrToRgb(value))} />
                    <Slider
                        label='Intensity'
                        precision={2} min={0} max={6}
                        value={props.lightData.intensity}
                        setProperty={(value: number) => props.setProperty('light.intensity', value)} />
                    <Toggle
                        label='Cast Shadow'
                        value={props.lightData.shadow}
                        setProperty={(value: boolean) => props.setProperty('light.shadow', value)} />
                    <Toggle
                        label='Shadow Catcher'
                        value={props.shadowCatcherData.enabled}
                        setProperty={(value: boolean) => props.setProperty('shadowCatcher.enabled', value)} />
                    <Slider
                        label='Catcher Intensity'
                        precision={2} min={0} max={1}
                        value={props.shadowCatcherData.intensity}
                        setProperty={(value: number) => props.setProperty('shadowCatcher.intensity', value)} />
                </Container>
            </div>
        );
    }
}

class DebugPanel extends React.Component <{
    debugData: ObserverData['debug'],
    uiData: ObserverData['ui'],
    setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{
        debugData: ObserverData['debug'];
        uiData: ObserverData['ui'];
        setProperty: SetProperty; }>): boolean {
        return JSON.stringify(nextProps.debugData) !== JSON.stringify(this.props.debugData) ||
               JSON.stringify(nextProps.uiData) !== JSON.stringify(this.props.uiData);
    }

    render() {
        const renderModeOptions = [
            { t: 'Default', v: 'default' },
            { t: 'Lighting', v: 'lighting' },
            { t: 'Albedo', v: 'albedo' },
            { t: 'Emissive', v: 'emission' },
            { t: 'WorldNormal', v: 'world_normal' },
            { t: 'Metalness', v: 'metalness' },
            { t: 'Gloss', v: 'gloss' },
            { t: 'Ao', v: 'ao' },
            { t: 'Specularity', v: 'specularity' },
            { t: 'Opacity', v: 'opacity' },
            { t: 'Uv0', v: 'uv0' }
        ];

        const props = this.props;
        return (
            <div className='popup-panel-parent'>
                <Container class='popup-panel' flex hidden={props.uiData.active !== 'debug'}>
                    <div className='popup-panel-header'>
                        <Label text='Debug' class='popup-panel-heading' />
                        <Button 
                            class='popup-panel-close' 
                            icon='E132' 
                            onClick={() => props.setProperty('ui.active', null)}
                        />
                    </div>
                    <Select
                        label='Render Mode'
                        type='string'
                        options={renderModeOptions}
                        value={props.debugData.renderMode}
                        setProperty={(value: string) => props.setProperty('debug.renderMode', value)} />
                    <ToggleColor
                        label='Wireframe'
                        booleanValue={props.debugData.wireframe}
                        setBooleanProperty={(value: boolean) => props.setProperty('debug.wireframe', value)}
                        colorValue={rgbToArr(props.debugData.wireframeColor)}
                        setColorProperty={(value: number[]) => props.setProperty('debug.wireframeColor', arrToRgb(value))} />
                    <Toggle
                        label='Grid'
                        value={props.debugData.grid}
                        setProperty={(value: boolean) => props.setProperty('debug.grid', value)}/>
                    <Toggle
                        label='Axes'
                        value={props.debugData.axes}
                        setProperty={(value: boolean) => props.setProperty('debug.axes', value)} />
                    <Toggle
                        label='Skeleton'
                        value={props.debugData.skeleton}
                        setProperty={(value: boolean) => props.setProperty('debug.skeleton', value)} />
                    <Toggle
                        label='Bounds'
                        value={props.debugData.bounds}
                        setProperty={(value: boolean) => props.setProperty('debug.bounds', value)} />
                    <Slider
                        label='Normals'
                        precision={2}
                        min={0}
                        max={1}
                        setProperty={(value: number) => props.setProperty('debug.normals', value)}
                        value={props.debugData.normals} />
                    <Toggle
                        label='Normal Maps'
                        value={props.debugData.normalMaps}
                        setProperty={(value: boolean) => props.setProperty('debug.normalMaps', value)}
                    />
                    <Toggle
                        label='Stats'
                        value={props.debugData.stats}
                        setProperty={(value: boolean) => props.setProperty('debug.stats', value)}
                    />
                </Container>
            </div>
        );
    }
}

class ViewPanel extends React.Component <{
    sceneData: ObserverData['scene'],
    uiData: ObserverData['ui'],
    runtimeData: ObserverData['runtime'],
    setProperty: SetProperty,
    observerData: ObserverData }> {

    shouldComponentUpdate(nextProps: Readonly<{
        sceneData: ObserverData['scene'];
        uiData: ObserverData['ui'];
        setProperty: SetProperty;
        observerData: ObserverData; }>): boolean {
        return JSON.stringify(nextProps.sceneData) !== JSON.stringify(this.props.sceneData) ||
               JSON.stringify(nextProps.uiData) !== JSON.stringify(this.props.uiData) ||
               nextProps.observerData.screenshot.outputFormat !== this.props.observerData.screenshot.outputFormat ||
               nextProps.observerData.screenshot.resolution !== this.props.observerData.screenshot.resolution ||
               nextProps.observerData.screenshot.preserveCamera !== this.props.observerData.screenshot.preserveCamera ||
               nextProps.observerData.screenshot.softenEdges !== this.props.observerData.screenshot.softenEdges ||
               nextProps.observerData.screenshot.softenEdgesRadius !== this.props.observerData.screenshot.softenEdgesRadius ||
               nextProps.observerData.camera.uiCameraMode !== this.props.observerData.camera.uiCameraMode ||
               nextProps.observerData.aircraftRotation.pitch !== this.props.observerData.aircraftRotation.pitch ||
               nextProps.observerData.aircraftRotation.roll !== this.props.observerData.aircraftRotation.roll;
    }

    render() {
        const props = this.props;
        
        return (
            <div className='popup-panel-parent'>
                <Container id='view-panel' class='popup-panel' flex hidden={props.uiData.active !== 'view'}>
                    <div className='popup-panel-header'>
                        <Label text='Batch Screenshots' class='popup-panel-heading' />
                        <Button 
                            class='popup-panel-close' 
                            icon='E132' 
                            onClick={() => props.setProperty('ui.active', null)}
                        />
                    </div>
                    <div style={{ padding: '10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input 
                            type="checkbox" 
                            checked={props.observerData.screenshot.preserveCamera}
                            onChange={(e) => {
                                props.setProperty('screenshot.preserveCamera', e.target.checked);
                            }}
                            id="preserve-camera-checkbox"
                        />
                        <label htmlFor="preserve-camera-checkbox" style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Preserve Camera Mode & Rotation (single screenshots only)
                        </label>
                    </div>
                    <div style={{ padding: '10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input 
                            type="checkbox" 
                            checked={props.observerData.screenshot.softenEdges}
                            onChange={(e) => {
                                props.setProperty('screenshot.softenEdges', e.target.checked);
                            }}
                            id="soften-edges-checkbox"
                        />
                        <label htmlFor="soften-edges-checkbox" style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Soften Edges (reduces aliasing)
                        </label>
                    </div>
                    {props.observerData.screenshot.softenEdges && (
                        <div style={{ padding: '0 10px 10px 10px' }}>
                            <Label text={`Edge Softening Intensity: ${props.observerData.screenshot.softenEdgesRadius.toFixed(1)}`} />
                            <SliderInput
                                class='secondary'
                                min={0.5}
                                max={3.0}
                                step={0.5}
                                value={props.observerData.screenshot.softenEdgesRadius}
                                onChange={(value: number) => {
                                    props.setProperty('screenshot.softenEdgesRadius', value);
                                }}
                            />
                        </div>
                    )}
                    <Button
                        class='secondary'
                        text='TEST PREPARE VIEW FOR SCREENSHOTS'
                        onClick={() => {
                            if (window.viewer) window.viewer.testPrepareViewForScreenshot();
                        }}
                    />
                    <Label text='Screenshot Output Format' />
                    <SelectInput
                        class='secondary'
                        type='string'
                        options={[
                            { v: 'png', t: 'PNG' },
                            { v: 'webp', t: 'WebP' }
                        ]}
                        value={props.observerData.screenshot.outputFormat}
                        onChange={(value: string) => {
                            props.setProperty('screenshot.outputFormat', value);
                        }}
                    />
                    <Label text='Screenshot Resolution' />
                    <SelectInput
                        class='secondary'
                        type='string'
                        options={[
                            { v: 'thumbnail', t: 'Thumbnail (512×184)' },
                            { v: 'large', t: 'Large @2x (2048×736)' },
                            { v: 'all', t: 'All (Both Sizes)' }
                        ]}
                        value={props.observerData.screenshot.resolution}
                        onChange={(value: string) => {
                            props.setProperty('screenshot.resolution', value);
                        }}
                    />
                    <Button
                        class='secondary'
                        text='BATCH SCREENSHOTS - ALL LIVERIES'
                        onClick={() => {
                            if (window.viewer) window.viewer.downloadSideViewTransparentAllLiveries();
                        }}
                    />
                    <Button
                        class='secondary'
                        text='RESTORE NORMAL VIEW'
                        onClick={() => {
                            if (window.viewer) window.viewer.restoreNormalViewAfterTest();
                        }}
                    />
                    <Label text='Camera Mode' class='section-header' />
                    <Button
                        class='secondary'
                        text={`Switch to ${(props.observerData.camera.uiCameraMode || 'orbit') === 'orbit' ? '✈️ Free Cam' : '🎯 Orbit Cam'}`}
                        onClick={() => {
                            const currentMode = props.observerData.camera.uiCameraMode || 'orbit';
                            const newMode = currentMode === 'orbit' ? 'free' : 'orbit';
                            props.setProperty('camera.uiCameraMode', newMode);
                        }}
                    />
                </Container>
            </div>
        );
    }
}

class LiveryPanel extends React.Component <{
    liveriesData: ObserverData['liveries'],
    uiData: ObserverData['ui'],
    setProperty: SetProperty }> {

    shouldComponentUpdate(nextProps: Readonly<{ liveriesData: ObserverData['liveries']; uiData: ObserverData['ui']; setProperty: SetProperty; }>): boolean {
        return JSON.stringify(nextProps.liveriesData) !== JSON.stringify(this.props.liveriesData) ||
               nextProps.uiData.active !== this.props.uiData.active;
    }

    render() {
        const props = this.props;
        const { available, selected, loading } = props.liveriesData;

        return (
            <div className='popup-panel-parent'>
                <Container class='popup-panel' flex hidden={props.uiData.active !== 'liveries'}>
                    <Label text='Liveries' class='popup-panel-heading' />
                    {loading && <Label text='Switching livery...' class='livery-loading' />}
                    <div className='livery-grid'>
                        {available.map((liveryName: string) => (
                            <Button
                                key={liveryName}
                                class={selected === liveryName ? 'livery-button-selected' : 'livery-button'}
                                text={liveryName}
                                width={120}
                                height={32}
                                enabled={!loading}
                                onClick={() => {
                                    console.log('Livery button clicked:', liveryName);
                                    props.setProperty('liveries.selected', liveryName);
                                }}
                            />
                        ))}
                    </div>
                </Container>
            </div>
        );
    }
}

class AircraftRotationPanel extends React.Component <{
    observerData: ObserverData,
    uiData: ObserverData['ui'],
    setProperty: SetProperty }> {
    
    shouldComponentUpdate(nextProps: Readonly<{
        observerData: ObserverData;
        uiData: ObserverData['ui'];
        setProperty: SetProperty; }>): boolean {
        return JSON.stringify(nextProps.uiData) !== JSON.stringify(this.props.uiData) ||
               nextProps.observerData.aircraftRotation.pitch !== this.props.observerData.aircraftRotation.pitch ||
               nextProps.observerData.aircraftRotation.roll !== this.props.observerData.aircraftRotation.roll;
    }

    render() {
        const props = this.props;
        return (
            <div className='popup-panel-parent'>
                <Container class='popup-panel' flex hidden={props.uiData.active !== 'aircraft-rotation'}>
                    <div className='popup-panel-header'>
                        <Label text='Aircraft Rotation' class='popup-panel-heading' />
                        <Button 
                            class='popup-panel-close' 
                            icon='E132' 
                            onClick={() => props.setProperty('ui.active', null)}
                        />
                    </div>
                    <Slider
                        label='Pitch'
                        precision={1}
                        min={-90}
                        max={90}
                        value={props.observerData.aircraftRotation.pitch}
                        setProperty={(value: number) => props.setProperty('aircraftRotation.pitch', value)} />
                    <Slider
                        label='Roll'
                        precision={1}
                        min={-180}
                        max={180}
                        value={props.observerData.aircraftRotation.roll}
                        setProperty={(value: number) => props.setProperty('aircraftRotation.roll', value)} />
                    <Button
                        class='secondary'
                        text='RESET ROTATION'
                        onClick={() => {
                            props.setProperty('aircraftRotation.pitch', 0);
                            props.setProperty('aircraftRotation.roll', 0);
                        }}
                    />
                </Container>
            </div>
        );
    }
}

class AnimationControlsPanel extends React.Component <{
    observerData: ObserverData,
    uiData: ObserverData['ui'],
    setProperty: SetProperty }> {
    
    shouldComponentUpdate(nextProps: Readonly<{
        observerData: ObserverData;
        uiData: ObserverData['ui'];
        setProperty: SetProperty; }>): boolean {
        return JSON.stringify(nextProps.uiData) !== JSON.stringify(this.props.uiData);
    }

    render() {
        const props = this.props;
        const aircraftConfig = props.observerData.aircraftConfig?.configuration;
        const hasConfig = aircraftConfig && (aircraftConfig.animationDefinitions || aircraftConfig.AnimationDefinitions);

        return (
            <div className='popup-panel-parent'>
                <Container class='popup-panel' flex hidden={props.uiData.active !== 'animations'}>
                    <div className='popup-panel-header'>
                        <Label text='Animation Controls' class='popup-panel-heading' />
                        <Button 
                            class='popup-panel-close' 
                            icon='E132' 
                            onClick={() => props.setProperty('ui.active', null)}
                        />
                    </div>
                    {!hasConfig && (
                        <Label text='No aircraft configuration loaded' class='warning-text' />
                    )}
                    {hasConfig && (
                        <>
                            <Label text='Control Surfaces' class='section-header' />
                            <Slider
                                label='Ailerons'
                                precision={2}
                                min={0}
                                max={1}
                                value={0.5}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('ailerons', value);
                                }} />
                            <Slider
                                label='Elevator'
                                precision={2}
                                min={0}
                                max={1}
                                value={0.5}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('elevator', value);
                                }} />
                            <Slider
                                label='Rudder'
                                precision={2}
                                min={0}
                                max={1}
                                value={0.5}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('rudder', value);
                                }} />
                            <Slider
                                label='Nose Wheel'
                                precision={2}
                                min={0}
                                max={1}
                                value={0.5}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('nosewheel', value);
                                }} />
                            
                            <Label text='Landing Gear' class='section-header' />
                            <Slider
                                label='Gear'
                                precision={2}
                                min={0}
                                max={1}
                                value={1}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('gear', value);
                                }} />
                            <Slider
                                label='Suspension'
                                precision={2}
                                min={0}
                                max={1}
                                value={1}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('suspension', value);
                                }} />
                            
                            <Label text='High-Lift Devices' class='section-header' />
                            <Slider
                                label='Flaps'
                                precision={2}
                                min={0}
                                max={1}
                                value={0}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('flaps', value);
                                }} />
                            <Slider
                                label='Spoilers'
                                precision={2}
                                min={0}
                                max={1}
                                value={0}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('spoilers', value);
                                }} />
                            
                            <Label text='Engines' class='section-header' />
                            <Slider
                                label='Throttle'
                                precision={2}
                                min={0}
                                max={1}
                                value={0}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('throttle', value);
                                }} />
                            
                            <Label text='Wing Flex' class='section-header' />
                            <Slider
                                label='Wing Flex'
                                precision={2}
                                min={0}
                                max={1}
                                value={0.5}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('wingflex', value);
                                }} />
                            
                            <Label text='Special Actions' class='section-header' />
                            <Slider
                                label='Doors'
                                precision={2}
                                min={0}
                                max={1}
                                value={0}
                                setProperty={(value: number) => {
                                    if (window.viewer) window.viewer.setAnimationGroup('doors', value);
                                }} />
                            
                            <div className='popup-panel-divider' />
                            <Button
                                class='secondary'
                                text='RESET ALL TO NEUTRAL'
                                onClick={() => {
                                    if (window.viewer) {
                                        window.viewer.setAnimationGroup('ailerons', 0.5);
                                        window.viewer.setAnimationGroup('elevator', 0.5);
                                        window.viewer.setAnimationGroup('rudder', 0.5);
                                        window.viewer.setAnimationGroup('nosewheel', 0.5);
                                        window.viewer.setAnimationGroup('flaps', 0);
                                        window.viewer.setAnimationGroup('spoilers', 0);
                                        window.viewer.setAnimationGroup('throttle', 0);
                                        window.viewer.setAnimationGroup('wingflex', 0.5);
                                        window.viewer.setAnimationGroup('suspension', 1.0);
                                        window.viewer.setAnimationGroup('doors', 0);
                                    }
                                }}
                            />
                            <Button
                                class='secondary'
                                text='APPLY SCREENSHOT SETTINGS'
                                onClick={() => {
                                    if (window.viewer) window.viewer.applyScreenshotAnimationSettings();
                                }}
                            />
                        </>
                    )}
                </Container>
            </div>
        );
    }
}

export {
    CameraPanel,
    SkyboxPanel,
    LightPanel,
    DebugPanel,
    ViewPanel,
    LiveryPanel,
    AnimationControlsPanel,
    AircraftRotationPanel
};
